import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { coerceLabel } from '@/_shared/lib/coerce-label';
import { normalizeThousandsSeparators } from '@/_shared/lib/numeric';

import type { ColumnSorter, ValueComparator } from './types';

dayjs.extend(utc);

export function rowValue<T>(row: T, id: string): unknown {
  return (row as Record<string, unknown>)[id];
}

export function defaultCell(value: unknown): React.ReactNode {
  return value == null ? '–' : coerceLabel(value);
}

export function alignClass(align: 'left' | 'right' | 'center' | undefined): string {
  return align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
}

function escapeCsv(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

// A spreadsheet evaluates a cell that begins with =, +, -, @ (or a leading tab/CR) as a formula, so
// untrusted (agent-authored) text there can run a formula on open. Quoting alone doesn't help — Excel
// strips CSV quotes first. Prefix such cells with a single quote so they import as text (CSV injection guard).
const CSV_FORMULA_TRIGGER = /^[=+\-@\t\r]/;
// Leave plain numbers — including negatives and decimals — untouched so numeric columns still export cleanly.
const CSV_NUMERIC = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

function neutralizeCsvFormula(value: string): string {
  if (!CSV_FORMULA_TRIGGER.test(value) || CSV_NUMERIC.test(value)) return value;
  return `'${value}`;
}

/**
 * Serialize header labels + a matrix of pre-stringified cells into CSV. Pass
 * `hardenFormulas` for untrusted content to neutralize spreadsheet formula injection.
 */
export function toCsv(headers: string[], rows: string[][], opts?: { hardenFormulas?: boolean }): string {
  const cell = opts?.hardenFormulas ? (v: string) => escapeCsv(neutralizeCsvFormula(v)) : escapeCsv;
  const headerLine = headers.map(cell).join(',');
  const bodyLines = rows.map((row) => row.map(cell).join(','));
  return [headerLine, ...bodyLines].join('\n');
}

export function parseTimestamp(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = dayjs.utc(value as string | number);
  return parsed.isValid() ? parsed.toDate() : null;
}

export function parseNumeric(value: unknown): number {
  const str = coerceLabel(value).trim();
  if (str === '') return Number.NaN;
  return Number(normalizeThousandsSeparators(str));
}

const alphabetical: ValueComparator = (a, b) => coerceLabel(a).localeCompare(coerceLabel(b));

const numeric: ValueComparator = (a, b) => {
  const na = parseNumeric(a);
  const nb = parseNumeric(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return coerceLabel(a).localeCompare(coerceLabel(b));
};

const timestamp: ValueComparator = (a, b) => {
  const ta = parseTimestamp(a)?.getTime() ?? null;
  const tb = parseTimestamp(b)?.getTime() ?? null;
  if (ta === null && tb === null) return 0;
  if (ta === null) return -1;
  if (tb === null) return 1;
  return ta - tb;
};

const SORTERS: Record<ColumnSorter, ValueComparator> = { alphabetical, numeric, timestamp };

export function resolveSorter(sorter: ColumnSorter | ValueComparator | undefined): ValueComparator {
  if (typeof sorter === 'function') return sorter;
  return SORTERS[sorter ?? 'alphabetical'];
}
